import { buildMemberPath, type MemberTabKey } from '../member/memberAppRouting'
import {
  parseWorkspaceDeepLink,
  type OpenWorkspaceTargetArgs,
} from '../services/workspaceSwitch/openWorkspaceTarget'

export type NativeWrapperRole = 'helper' | 'member'

// Env-suffixed applicationIds (`.sit`, `.prod`, …) share the same base
// route as the un-suffixed DEV id. Prefix match handles them all so a
// new gradle flavor never needs a code change here.
const NATIVE_APP_INITIAL_ROUTE_BY_PREFIX: ReadonlyArray<[string, string]> = [
  ['com.timelining.helper.app', '/helper/dashboard'],
  ['com.timelining.helper.stable', '/helper/dashboard'],
  ['com.timelining.member.app', '/member'],
  ['com.timelining.member.stable', '/member'],
]

const initialRouteForAppId = (appId: string): string | undefined => {
  for (const [prefix, route] of NATIVE_APP_INITIAL_ROUTE_BY_PREFIX) {
    if (appId === prefix || appId.startsWith(`${prefix}.`)) return route
  }
  return undefined
}

export const isNativeWrapperPath = (appId: string, pathname: string): boolean => {
  if (appId.includes('.helper.')) {
    return pathname === '/join' ||
      pathname.startsWith('/helper') ||
      pathname.startsWith('/invite/') ||
      pathname.startsWith('/invite-review/') ||
      pathname.startsWith('/join/')
  }
  if (appId.includes('.member.')) {
    return pathname === '/join' ||
      pathname.startsWith('/member') ||
      pathname.startsWith('/invite-review/') ||
      pathname.startsWith('/join/')
  }
  return false
}

export const shouldApplyNativeInitialRoute = (
  appId: string,
  pathname: string,
  publicSitePaths: ReadonlySet<string>,
): boolean => {
  if (isNativeWrapperPath(appId, pathname)) return false
  return pathname === '/' || pathname === '/console' || publicSitePaths.has(pathname)
}

export const getNativeInitialRoute = (
  appId: string,
  pathname: string,
  publicSitePaths: ReadonlySet<string>,
): string | null => {
  const targetPath = initialRouteForAppId(appId)
  return targetPath && shouldApplyNativeInitialRoute(appId, pathname, publicSitePaths)
    ? targetPath
    : null
}

// Inside a native wrapper there is no public marketing site to fall back to, so
// a member-shell dead end (signed out, or a slug that matches no workspace) must
// stay on the wrapper's own entry route instead of bouncing to '/'. On the web,
// '/' is still the right answer.
export const resolveMemberShellFallbackPath = (
  wrapperRole: NativeWrapperRole | null,
): string => (wrapperRole === 'member' ? '/member' : '/')

export type NativeRouteLockInput = {
  wrapperRole: NativeWrapperRole | null
  pathname: string
  isAuthenticated: boolean
  isHelperUser: boolean
  firstTeamSlug?: string | null
  workspaces?: ReadonlyArray<{ id: string; slug?: string; isPersonal?: boolean; kind?: string; ownerId?: string }>
  openWorkspaceTarget?: (args: OpenWorkspaceTargetArgs) => Promise<boolean | void> | boolean | void
}

export const resolveNativeRouteLockRedirect = ({
  wrapperRole,
  pathname,
  isAuthenticated,
  isHelperUser,
  firstTeamSlug,
  workspaces,
  openWorkspaceTarget,
}: NativeRouteLockInput): string | null => {
  if (!wrapperRole) return null

  if (wrapperRole === 'helper') {
    if (pathname === '/join' || pathname.startsWith('/helper/') || pathname.startsWith('/invite/') || pathname.startsWith('/invite-review/')) {
      return null
    }
    return '/helper/dashboard'
  }

  if (!isAuthenticated) return null
  if (isHelperUser) return null
  if (pathname === '/join' || pathname.startsWith('/member/') || pathname.startsWith('/invite-review/')) return null

  // /w/{id}/... route lock handling
  if (pathname.startsWith('/w/')) {
    const parsed = parseWorkspaceDeepLink(pathname)
    const matchedWs = workspaces?.find(
      (w) => w.id === parsed?.workspaceId || w.slug === parsed?.workspaceId,
    )
    if (matchedWs) {
      const slug = matchedWs.slug || matchedWs.id
      let targetTab: MemberTabKey = 'for-you'

      if (parsed) {
        switch (parsed.targetKind) {
          case 'chat':
          case 'meeting':
            targetTab = 'nexus'
            break
          case 'item':
          case 'checklist':
            targetTab = 'board'
            break
          case 'form':
            targetTab = 'forms'
            break
          default:
            targetTab = 'for-you'
            break
        }
      }

      if (openWorkspaceTarget) {
        void openWorkspaceTarget({
          workspaceId: matchedWs.id,
          target: parsed?.target,
          source: 'DEEPLINK',
        })
      }

      return buildMemberPath({ workspace: slug, tab: targetTab })
    }
  }

  // legacy /workspace/{slug}/... route lock handling
  if (pathname.startsWith('/workspace/')) {
    const parts = pathname.replace(/^\/workspace\/?/, '').split('/')
    const wsSlugOrId = parts[0]
    const matchedWs = workspaces?.find(
      (w) => w.slug === wsSlugOrId || w.id === wsSlugOrId,
    )
    if (matchedWs) {
      const slug = matchedWs.slug || matchedWs.id
      const sub = parts[1]
      let targetTab: MemberTabKey = 'for-you'
      if (sub === 'chat' || sub === 'meetings') {
        targetTab = 'nexus'
      } else if (sub === 'forms') {
        targetTab = 'forms'
      } else if (sub === 'dashboard' || sub === 'board') {
        targetTab = 'board'
      }

      if (openWorkspaceTarget) {
        void openWorkspaceTarget({
          workspaceId: matchedWs.id,
          target: sub,
          source: 'DEEPLINK',
        })
      }

      return buildMemberPath({ workspace: slug, tab: targetTab })
    }
  }

  return firstTeamSlug ? buildMemberPath({ workspace: firstTeamSlug, tab: 'for-you' }) : null
}
